import { useMemo } from 'react';

import { orientationMap } from '../../../../models';
import { aggregators } from '../../../../utils';
import type { PieProps } from '../pie.model';

type UsePieOptionsArgs = Pick<PieProps, 'options' | 'activeControls' | 'params' | 'data'> & {
    width: number;
    height: number;
    legendDimensions: { w: number; h: number };
};

const MARGIN = 24;
const TOTAL_MARGIN = MARGIN * 2;

const usePieOptions = ({
    options,
    activeControls,
    params,
    data: rawData,
    width,
    height,
    legendDimensions,
}: UsePieOptionsArgs) => {
    const dataProps = useMemo(() => {
        const activeGroupParam = params?.group ?? activeControls?.groups?.initialValue;
        const activeGroup = options.groups?.find?.(group => group.name === activeGroupParam) ?? options.groups?.[0];
        const partitions = activeGroup?.partitions;
        const data = rawData[0];

        let aggregrate = 0;

        if (partitions?.length) {
            try {
                const sum = aggregators.sum(partitions, part => {
                    const value = data?.[part.column];
                    if (typeof value === 'number' || isFinite(+value)) {
                        return value;
                    }
                    return 0;
                });
                if (sum) {
                    aggregrate = sum;
                }
            } catch (err) {
                console.error('Failed to calculate pie aggregate', err);
            }
        }

        return {
            partitions,
            data,
            aggregrate,
        };
    }, [activeControls, options.groups, params?.group, rawData]);

    const positionProps = useMemo(() => {
        let legendPosition = options.legend.position;

        const isTop = legendPosition === 'top';
        const isBottom = legendPosition === 'bottom';
        const isLeft = legendPosition === 'left';
        const isRight = legendPosition === 'right';

        if (width < 385 && !isTop && !isBottom) {
            legendPosition = 'none';
        }

        const pieWidth = width - (isLeft || isRight ? legendDimensions.w : 0) - TOTAL_MARGIN;
        const pieHeight = height - (isTop || isBottom ? legendDimensions.h : 0) - TOTAL_MARGIN;

        const radius = Math.min(pieWidth, pieHeight) / 2;
        const innerRadius = options.isDonut ? radius / 2 : 0.025;

        let left = width / 2,
            top = height / 2,
            containerWidth = width,
            containerHeight = height,
            marginLeft = 0,
            marginTop = 0;

        if (isRight || isLeft) {
            containerWidth = width - legendDimensions.w;
            left = pieWidth / 2 + MARGIN;
            if (isLeft) {
                marginLeft = legendDimensions.w;
            }
        }

        if (isTop || isBottom) {
            containerHeight = height - legendDimensions.h;
            left = width / 2;
            top = MARGIN + pieHeight / 2;

            if (isTop) {
                marginTop = legendDimensions.h;
            }
        }

        return {
            container: {
                width: containerWidth,
                height: containerHeight,
                marginLeft,
                marginTop,
            },
            translate: { top, left },
            radius: { inner: innerRadius, outer: radius },
            orientation: orientationMap[legendPosition],
            legendPosition,
        };
    }, [options.legend.position, options.isDonut, width, height, legendDimensions.w, legendDimensions.h]);

    return {
        ...dataProps,
        ...positionProps,
        showPartitionLabels: options.showPartitionLabels,
    };
};

type PieOptions = ReturnType<typeof usePieOptions>;

export type { PieOptions };
export { usePieOptions };
